/**
 * The goal's orchestration tree (app-clarity `dl/goal`, `ide/ide-project`): the goal at the root,
 * then each lane with its latest upward update, then the lane's runs, questions and next queued
 * tasks. Blocked lanes float to the top, then lanes that need you, then running, queued and
 * landed lanes; idle lanes fold into one line. "Updates to the goal" lists what the lanes reported
 * (questions asked, work landed), newest first. Pure over the Lanes model, so it is table-tested.
 */

import { isIdleLane } from './lanes-layout.js'
import type { AskRow, Lane, LaneCard, LanesModel } from './lanes-model.js'

export type TreeStatus = 'blocked' | 'wait' | 'run' | 'idle' | 'ok'

export interface TreeChild {
  key: string
  kind: 'run' | 'question' | 'task'
  status: TreeStatus
  title: string
  meta: string
  /** Running: the run to open or take over. */
  runId?: string
  /** Question: the inspector key that answers it. */
  answerKey?: string
  /** Task: the inspector key that opens it. */
  taskKey?: string
}

export interface TreeLane {
  id: string
  name: string
  pseudo: boolean
  status: TreeStatus
  /** The lane's latest upward update, one line. */
  update: string
  /** Blocked: the reason in words (the status word is coloured, the reason is not). */
  blockedReason?: string
  /** Blocked: the inspector key of the card to fix. */
  fixKey?: string
  children: TreeChild[]
  /** Queued tasks not listed as children. */
  moreQueued: number
}

export interface TreeUpdate {
  key: string
  at: number
  lane: string
  text: string
  /** Inspector key the update opens. */
  open: string
}

export interface GoalTree {
  lanes: TreeLane[]
  idle: Lane[]
  updates: TreeUpdate[]
  counts: { lanes: number; running: number; needs: number; blocked: number }
}

const RANK: Record<TreeStatus, number> = { blocked: 0, wait: 1, run: 2, idle: 3, ok: 4 }
const QUEUED_SHOWN = 2

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

function laneCards(model: LanesModel, lane: Lane): LaneCard[] {
  const fromColumns = Object.values(lane.columns).flat()
  const seen = new Set(fromColumns.map((c) => c.id))
  return [
    ...fromColumns,
    ...model.cards.filter((c) => c.workstreamId === lane.id && !seen.has(c.id)),
  ]
}

function laneAsks(model: LanesModel, lane: Lane): AskRow[] {
  return model.asks.filter(
    (a) => a.workstreamId === lane.id || (!a.workstreamId && a.workstreamName === lane.name),
  )
}

/** A blocker in the asks (environment, landing): the lane can't continue until it's fixed. */
export function blockerReason(a: Pick<AskRow, 'title' | 'question'>): string | undefined {
  const t = a.title || a.question
  const m = /^(?:landing )?blocked(?::\s*(?:[a-z]+\s+—\s*)?)?\s*(.*)$/i.exec(t)
  return m ? m[1] || 'needs a fix' : undefined
}

/** A lane's agent line without the "Waiting for you:" prefix the status already says. */
export function stripWaiting(line: string): string {
  return line.replace(/^(Waiting for you:\s*)+/i, '')
}

function treeLane(model: LanesModel, lane: Lane): TreeLane {
  const cards = laneCards(model, lane)
  const all = laneAsks(model, lane)
  const blockers = all.filter((a) => blockerReason(a) !== undefined)
  const asks = all.filter((a) => blockerReason(a) === undefined)
  const blocked = cards.find((c) => c.state === 'blocked')
  const blocker = blockers[0]
  const waiting = lane.agents.some((a) => a.waiting) || asks.length > 0
  const queued = lane.columns.queued
  const landed = lane.columns.landed
  const status: TreeStatus =
    blocked || blocker
      ? 'blocked'
      : waiting
        ? 'wait'
        : lane.agents.length
          ? 'run'
          : queued.length
            ? 'idle'
            : landed.length
              ? 'ok'
              : 'idle'
  const agent = lane.agents[0]
  const update = blocker
    ? (blockerReason(blocker) as string)
    : blocked
      ? (blocked.reason ?? blocked.subLabel ?? 'needs a fix')
      : asks[0]
        ? `asks you: ${asks[0].question || asks[0].title}`
        : agent
          ? stripWaiting(agent.line)
          : landed.length
            ? `Landed ${landed.length} today${landed[0] ? `; last: ${landed[0].title}` : ''}`
            : queued.length
              ? `${plural(queued.length, 'task')} queued${lane.idleReason ? ` · ${lane.idleReason}` : ''}`
              : (lane.idleReason ?? (lane.paused ? 'Paused' : 'No agent now'))
  const children: TreeChild[] = []
  for (const a of lane.agents)
    children.push({
      key: `run:${a.runId}`,
      kind: 'run',
      status: a.waiting ? 'wait' : 'run',
      title: a.title,
      meta: `${a.label} · ${a.phaseLabel}`,
      runId: a.runId,
    })
  for (const b of blockers)
    children.push({
      key: `ask:${b.id}`,
      kind: 'task',
      status: 'blocked',
      title: blockerReason(b) as string,
      meta: 'blocked · fix it to continue',
      taskKey: `ask:${encodeURIComponent(b.id)}`,
    })
  for (const q of asks)
    children.push({
      key: `ask:${q.id}`,
      kind: 'question',
      status: 'wait',
      title: q.locked ? 'Encrypted: unlock this browser to read' : q.question || q.title,
      meta: `${q.kind === 'approval' ? 'approval' : 'question'} for you${q.blocking ? ' · the run waits' : ''}`,
      answerKey: `ask:${encodeURIComponent(q.id)}`,
    })
  if (blocked)
    children.push({
      key: `task:${blocked.id}`,
      kind: 'task',
      status: 'blocked',
      title: blocked.title,
      meta: blocked.reason ?? 'blocked',
      taskKey: `task:${blocked.id}`,
    })
  for (const c of queued.slice(0, QUEUED_SHOWN))
    children.push({
      key: `task:${c.id}`,
      kind: 'task',
      status: 'idle',
      title: c.title,
      meta: c.proposed ? 'proposed · approve to queue' : 'queued',
      taskKey: `task:${c.id}`,
    })
  return {
    id: lane.id,
    name: model.locked ? 'Locked lane' : lane.name,
    pseudo: !!lane.pseudo,
    status,
    update,
    ...(blocker
      ? { blockedReason: update, fixKey: `ask:${encodeURIComponent(blocker.id)}` }
      : blocked
        ? { blockedReason: update, fixKey: `task:${blocked.id}` }
        : {}),
    children,
    moreQueued: Math.max(0, queued.length - QUEUED_SHOWN),
  }
}

export function buildGoalTree(model: LanesModel): GoalTree {
  // A lane that landed today reports up (concept: "Release pipeline · Landed 2 today").
  const quiet = (l: Lane) => isIdleLane(l) && !l.columns.landed.length && !laneAsks(model, l).length
  const active = model.lanes.filter((l) => !quiet(l))
  const idle = model.lanes.filter(quiet)
  const lanes = active
    .map((l, i) => ({ l: treeLane(model, l), i }))
    .sort((a, b) => RANK[a.l.status] - RANK[b.l.status] || a.i - b.i)
    .map((x) => x.l)
  const updates: TreeUpdate[] = []
  for (const a of model.asks)
    updates.push({
      key: `ask:${a.id}`,
      at: a.createdAt,
      lane: a.workstreamName ?? model.name,
      text:
        blockerReason(a) !== undefined
          ? `blocked: ${blockerReason(a)}`
          : `asks you: ${a.locked ? 'an encrypted question' : a.question || a.title}`,
      open: `ask:${encodeURIComponent(a.id)}`,
    })
  for (const c of model.cards)
    if (c.landedAt)
      updates.push({
        key: `landed:${c.id}`,
        at: c.landedAt,
        lane: c.laneName,
        text: `Landed ${c.title}`,
        open: `task:${c.id}`,
      })
  updates.sort((a, b) => b.at - a.at)
  return {
    lanes,
    idle,
    updates: updates.slice(0, 8),
    counts: {
      lanes: active.length,
      running: model.lanes.reduce((n, l) => n + l.agents.length, 0),
      needs: model.counts.needs,
      blocked: lanes.filter((l) => l.status === 'blocked').length,
    },
  }
}
